// @flow
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Replay as RetryIcon,
  Done as DoneIcon,
  DoneAll as DoneAllIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon
} from '@material-ui/icons';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SignProcess from '../model/SignProcess';
import {QRCodeCanvas} from 'qrcode.react';
import ApiResponse from '../model/ApiResponse';
import { useSnackbar } from 'notistack';
import { AuthContext } from '../context/auth';

const useStyles = makeStyles((theme) => ({
  draft: {
    color: theme.palette.secondary[ '900' ],
  },
  grey: {
    color: theme.palette.text.secondary,
  },
  error: {
    color: theme.palette.error.main,
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  upload: {
    display: 'none',
  },
}));

type Props = {
  code: string,
  process: SignProcess,
  expanded: boolean,
  disabled: boolean,
  processing: boolean,
  onExpanded: (string) => (event: any, isExpanded: boolean) => void,
  onSave: ({ file: File }) => Promise<any>,
};

const ProcessVideoData = (props: Props) => {
  const { doGet } = useContext(AuthContext);
  const { t } = useTranslation();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { code, process, expanded, onExpanded, disabled, processing } = props;

  const [termsChecked, setTermsChecked] = useState<boolean>(false);
  const [loading, setLoading] = useState<bool>(false);
  const [selfieUrl, setSelfieUrl] = useState<?string>();
  const [qrSelfieUrl, setQrSelfieUrl] = useState<?string>();
  const [validationUrl, setValidationUrl] = useState<?string>();
  const [qrValidationUrl, setQrValidationUrl] = useState<?string>();

  let processStatusText = process.hasVideoLoaded() ? 'Completed' : 'Not completed';
  let processStatusIcon = process.hasVideoLoaded() ? <DoneIcon color="action"/> : <EditIcon color="secondary"/>;
  if (process.hasDataValidated()) {
    processStatusText = 'Validated';
    processStatusIcon = <DoneAllIcon color="primary"/>;
  }

  const loadData = () => {
    setLoading(true);
    const path = process.internalValidation ? 'selfie' : 'validation';
    doGet(`/customer/sign-process/${process.code}/${path}-url`)
      .then((response: ApiResponse) => {
        if (response.ok) {
          if (process.internalValidation) {
            setSelfieUrl(String(response.data));
            setQrSelfieUrl(String(response.data));
          } else {
            setValidationUrl(String(response.data));
            setQrValidationUrl(String(response.data));
          }
        } else if (!response.is401Error()) {
          enqueueSnackbar(t(response.message), {
            variant: 'error',
          });
        }
        setLoading(false);
      });
  };

  const haveTerms = !process.internalValidation && !termsChecked && !process.hasVideoLoaded();
  const canReload = !loading && !processing && !disabled && !selfieUrl && !validationUrl && (process.internalValidation || termsChecked);

  return (
    <Accordion
      key={code}
      expanded={expanded}
      onChange={onExpanded(code)}
      TransitionProps={{ unmountOnExit: true }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon/>}
        aria-controls={`${code}-panel`}
        id={`${code}-panel`}
      >
        {processStatusIcon}&nbsp;
        <Typography className={classes.heading}>
          {t(process.internalValidation ? 'Verification video' : 'Validation process')}
        </Typography>
        <Typography className={classes.secondaryHeading}>
          {t(processStatusText)}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={1}>
          {(processing || loading) && <Grid item xs={12} sm={12} md={12} lg={12}>
            <CircularProgress />
          </Grid>}
          {haveTerms && <Grid item xs={12} sm={12} md={12} lg={12}>
            <FormControlLabel
              control={<Checkbox
                disabled={disabled}
                checked={termsChecked}
                onChange={(e) => {
                  loadData();
                  setTermsChecked(e.target.checked);
                }}
                name='terms'
                color='primary' />}
              label={<a href='https://ecuafirma.com/condiciones-generales-ac/' target='_blank' rel='noopener noreferrer'>
                {t('Accept terms and conditions')}
              </a>} />
          </Grid>}
          {canReload && <Grid item xs={12} sm={12} md={12} lg={12}>
            <Button
              disabled={disabled}
              variant='contained'
              color='primary'
              startIcon={<RetryIcon />}
              aria-label='reload'
              onClick={loadData}
            >
              {t('Load')}
            </Button>
          </Grid>}
          {(process.internalValidation && !disabled) && <Grid item xs={12} sm={12} md={12} lg={12}>
            <b>{t('Selfie intro')}</b><br/>
            <b>{t('Image intro')}</b>
          </Grid>}
          {(process.internalValidation && !disabled && selfieUrl) && <Grid item xs={12} sm={12} md={12} lg={12}>
            <a href={selfieUrl} target='_blank' rel='noopener noreferrer'>
              {t('Browse selfie link')}
            </a>
            <br/>
            <br/>
            <b>{t('Browse selfie qr')}&nbsp;&nbsp;</b>
            <br/>
            <br/>
            <QRCodeCanvas
              value={qrSelfieUrl}
              size='64'
              bgColor="#FFFFFF"
              fgColor="#000000"
              includeMargin={false}
              level="L" />
          </Grid>}
          {(!process.internalValidation && !disabled && validationUrl) && <Grid item xs={12} sm={12} md={12} lg={12}>
            <Button
              size='small'
              variant='outlined'
              color='primary'
              onClick={() => {
                window.open(validationUrl, '_blank');
              }}>
              {t('Browse validation link')}
            </Button>
            <br/>
            <br/>
            <b>{t('Browse validation qr')}&nbsp;&nbsp;</b>
            <br/>
            <br/>
            <QRCodeCanvas
              value={qrValidationUrl}
              size='64'
              bgColor="#FFFFFF"
              fgColor="#000000"
              includeMargin={false}
              level="L" />
          </Grid>}
          {/*<Grid item xs={12} sm={12} md={12} lg={12}>*/}
          {/*  {!disabled && <><b>{t('Selfie not posible')}</b><br/><br/></>}*/}
          {/*  <input*/}
          {/*    accept="image/*"*/}
          {/*    className={classes.upload}*/}
          {/*    id="verification-video"*/}
          {/*    type="file"*/}
          {/*    onChange={(e) => {*/}
          {/*      if (e.target.files*/}
          {/*          && e.target.files.length) {*/}
          {/*        const file = e.target.files[ 0 ];*/}
          {/*        if (file.size >= maxSize) {*/}
          {/*          enqueueSnackbar(t('Upload limit is MB_SIZE', { size: (maxSize / (1024 * 1024)).toFixed(0) }), {*/}
          {/*            variant: 'error',*/}
          {/*          });*/}
          {/*        } else {*/}
          {/*          setFile(file);*/}
          {/*        }*/}
          {/*      }*/}
          {/*    }}*/}
          {/*  />*/}
          {/*  <label htmlFor="verification-video">*/}
          {/*    {!file && <Button*/}
          {/*      disabled={disabled}*/}
          {/*      variant="contained"*/}
          {/*      color="secondary"*/}
          {/*      startIcon={<AddIcon/>}*/}
          {/*      component="span">*/}
          {/*      {t(`Upload verification video`)}*/}
          {/*    </Button>}*/}
          {/*    {file && <span>*/}
          {/*    <Typography variant="subtitle2" className={classes.grey}>*/}
          {/*      {t('Verification video')}&nbsp;*/}
          {/*      <Button*/}
          {/*        disabled={processing || disabled}*/}
          {/*        variant="contained"*/}
          {/*        color="primary"*/}
          {/*        startIcon={processing ? <CircularProgress color="inherit" size="1rem" /> : <UploadIcon/>}*/}
          {/*        aria-label="upload"*/}
          {/*        onClick={() => {*/}
          {/*          onSave({*/}
          {/*            file: file,*/}
          {/*          }).then((ok: boolean) => {*/}
          {/*            if (ok) {*/}
          {/*              setFile(null);*/}
          {/*            }*/}
          {/*          });*/}
          {/*        }}*/}
          {/*      >*/}
          {/*        {t('Upload')}*/}
          {/*      </Button>*/}
          {/*      <IconButton*/}
          {/*        disabled={processing || disabled}*/}
          {/*        aria-label="delete"*/}
          {/*        className={classes.error}*/}
          {/*        onClick={() => {*/}
          {/*          setFile(null);*/}
          {/*        }}>*/}
          {/*        <DeleteIcon/>*/}
          {/*      </IconButton>*/}
          {/*    </Typography>*/}
          {/*  </span>}*/}
          {/*  </label>*/}
          {/*</Grid>*/}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default ProcessVideoData;
